import { debug } from '@skinsoft/debug';
import { createMiddlewareChainFacade } from '@skinsoft/middleware-chain';
import { interval, Observable } from 'rxjs';
import { shareReplay, tap, timeout } from 'rxjs/operators';

import * as fetch from './fetch';
import { FetchError } from './types';

export class Proxy {
    public static readonly onError = createMiddlewareChainFacade<FetchError>();
    private static debug = debug('proxy');

    private deduplicateRequests: boolean;
    private inFlightRequests: { [key: string]: Observable<any> } = {};
    private deletionQueue = new Set<string>();

    constructor(deduplicateRequests: boolean = true, recyclingInterval: number = 1e3) {
        this.deduplicateRequests = deduplicateRequests;
        interval(recyclingInterval).subscribe(() => {
            for (const dedupId of this.deletionQueue) {
                delete this.inFlightRequests[dedupId];
                this.deletionQueue.delete(dedupId);
            }
        });
    }

    public fetch(
        uri: string,
        query: Record<string, any> = {},
        method?: string,
        body?: Record<string, any>,
        forceRequest?: boolean,
    ): Observable<any> {
        const search = new URLSearchParams(query).toString();
        const url = search.length > 0 ? `${uri}?${search}` : uri;

        const dedupId = `${url}/${method}/${JSON.stringify(body)}`;

        Proxy.debug('starting fetch', uri, query);

        if (this.deduplicateRequests && this.inFlightRequests[dedupId] && !forceRequest) {
            Proxy.debug('deduplicating request', uri, query);
            return this.inFlightRequests[dedupId];
        }

        const init: RequestInit = {};

        if (method) {
            init.method = method;
        }

        if (body) {
            init.headers = {
                'Content-Type': 'application/json',
            };
            init.body = JSON.stringify(body);
        }

        const request$ = fetch.api(url, init).pipe(
            timeout(30e3),
            tap(
                response => {
                    Proxy.debug('fetch succeeded', response, uri, query);
                    if (this.deletionQueue.has(dedupId)) {
                        // this might happen when the fetch call has forceRequest to true
                        this.deletionQueue.delete(dedupId);
                    }

                    this.deletionQueue.add(dedupId);
                },
                error => {
                    Proxy.debug('fetch failed', error, uri, query);
                    delete this.inFlightRequests[dedupId];

                    Proxy.onError.run(error);
                },
            ),
        );

        this.inFlightRequests[dedupId] = request$.pipe(shareReplay(1));

        return this.inFlightRequests[dedupId];
    }
}
